import Phaser from 'phaser';

/**
 * Pan + zoom controller for the game scene.
 *
 *  - Mouse wheel: zoom around the cursor.
 *  - Click-drag on empty space: pan.
 *  - Touch one-finger drag on empty space: pan.
 *  - Touch pinch (two fingers): zoom around the midpoint.
 *
 * Drags that start on an interactive node are NOT captured — those go
 * through to the node, so the breach-tap still works as expected.
 *
 * The "empty space" capture is implemented by a low-depth interactive
 * Zone that sits behind all gameplay objects. Phaser's topOnly input
 * routing means interactive nodes (added later, on top) intercept their
 * own taps first; only when the pointer lands on no node does this rig
 * see the pointerdown.
 */
/** Absolute lowest zoom, so a runaway board cannot shrink the view to a speck. */
const HARD_MIN_ZOOM = 0.12;

export class CameraRig {
  private scene: Phaser.Scene;
  private bgZone: Phaser.GameObjects.Zone;
  private dragStart?: { x: number; y: number; scrollX: number; scrollY: number };
  private bounds: { minX: number; maxX: number; minY: number; maxY: number };
  private minZoom: number;
  private maxZoom = 2.0;

  // pinch state
  private pinchStartDist = 0;
  private pinchStartZoom = 1;
  private pinchMid = { x: 0, y: 0 };

  private levelBounds: { minX: number; minY: number; maxX: number; maxY: number };

  constructor(scene: Phaser.Scene, levelBounds: { minX: number; minY: number; maxX: number; maxY: number }) {
    this.scene = scene;
    this.levelBounds = levelBounds;
    const { width, height } = scene.scale;
    const pad = 330;
    this.bounds = {
      minX: levelBounds.minX - pad,
      maxX: levelBounds.maxX + pad,
      minY: levelBounds.minY - pad,
      maxY: levelBounds.maxY + pad,
    };
    const fitX = width / (this.bounds.maxX - this.bounds.minX);
    const fitY = height / (this.bounds.maxY - this.bounds.minY);
    // Floor low enough that even big procedural levels can be zoomed out far
    // enough to see the whole graph at once.
    this.minZoom = Math.max(0.22, Math.min(fitX, fitY, 1));

    this.bgZone = scene.add
      .zone(width / 2, height / 2, 6000, 6000)
      .setInteractive()
      .setScrollFactor(0)
      .setDepth(-50);

    this.bgZone.on('pointerdown', (p: Phaser.Input.Pointer) => this.onDown(p));
    scene.input.on('pointermove', (p: Phaser.Input.Pointer) => this.onMove(p));
    scene.input.on('pointerup', () => this.onUp());
    scene.input.on('pointerupoutside', () => this.onUp());

    // wheel zoom (PC)
    scene.input.on('wheel', (_p: unknown, _objs: unknown, _dx: number, dy: number) => {
      this.zoomBy(dy > 0 ? 0.9 : 1.1, scene.input.activePointer.x, scene.input.activePointer.y);
    });
  }

  private lastTapAt = 0;

  private onDown(p: Phaser.Input.Pointer) {
    // multi-touch: handle pinch start
    const ptrs = this.activePointers();
    if (ptrs.length >= 2) {
      this.startPinch(ptrs[0], ptrs[1]);
      return;
    }
    // Double-tap on empty space re-frames the whole level — a quick way to
    // snap back after panning around a big board (mirrors the recenter button).
    const now = this.scene.time.now;
    if (this.canPan() && now - this.lastTapAt < 320) {
      this.recenter();
      this.lastTapAt = 0;
      this.dragStart = undefined;
      return;
    }
    this.lastTapAt = now;
    const cam = this.scene.cameras.main;
    this.dragStart = { x: p.x, y: p.y, scrollX: cam.scrollX, scrollY: cam.scrollY };
  }

  private onMove(p: Phaser.Input.Pointer) {
    const ptrs = this.activePointers();
    if (ptrs.length >= 2 && this.pinchStartDist > 0) {
      this.updatePinch(ptrs[0], ptrs[1]);
      return;
    }
    if (!this.dragStart || !p.isDown) return;
    const cam = this.scene.cameras.main;
    const dx = (p.x - this.dragStart.x) / cam.zoom;
    const dy = (p.y - this.dragStart.y) / cam.zoom;
    cam.scrollX = this.dragStart.scrollX - dx;
    cam.scrollY = this.dragStart.scrollY - dy;
    this.clampScroll();
  }

  private onUp() {
    this.dragStart = undefined;
    if (this.activePointers().length < 2) {
      this.pinchStartDist = 0;
    }
  }

  private startPinch(p1: Phaser.Input.Pointer, p2: Phaser.Input.Pointer) {
    this.pinchStartDist = Phaser.Math.Distance.Between(p1.x, p1.y, p2.x, p2.y);
    this.pinchStartZoom = this.scene.cameras.main.zoom;
    this.pinchMid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
    this.dragStart = undefined;
  }

  private updatePinch(p1: Phaser.Input.Pointer, p2: Phaser.Input.Pointer) {
    const d = Phaser.Math.Distance.Between(p1.x, p1.y, p2.x, p2.y);
    if (d < 1) return;
    const newZoom = Phaser.Math.Clamp(
      this.pinchStartZoom * (d / this.pinchStartDist),
      this.minZoom, this.maxZoom,
    );
    this.zoomTo(newZoom, this.pinchMid.x, this.pinchMid.y);
  }

  private activePointers(): Phaser.Input.Pointer[] {
    const pm = this.scene.input.manager.pointers;
    return pm.filter((pt) => pt.isDown);
  }

  private zoomBy(factor: number, screenX: number, screenY: number) {
    const cam = this.scene.cameras.main;
    const z = Phaser.Math.Clamp(cam.zoom * factor, this.minZoom, this.maxZoom);
    this.zoomTo(z, screenX, screenY);
  }

  /**
   * Phaser zooms a camera about its own centre: the world rectangle on screen
   * starts at scroll + (width - width / zoom) / 2, not at scroll. These two
   * helpers convert between the scroll value and the top left corner of what
   * the camera really shows, so pan, zoom, clamp and framing all agree.
   */
  private viewOrigin(): { x: number; y: number } {
    const cam = this.scene.cameras.main;
    return {
      x: cam.scrollX + (cam.width - cam.width / cam.zoom) / 2,
      y: cam.scrollY + (cam.height - cam.height / cam.zoom) / 2,
    };
  }

  private setViewOrigin(x: number, y: number) {
    const cam = this.scene.cameras.main;
    cam.scrollX = x - (cam.width - cam.width / cam.zoom) / 2;
    cam.scrollY = y - (cam.height - cam.height / cam.zoom) / 2;
  }

  private zoomTo(newZoom: number, screenX: number, screenY: number) {
    const cam = this.scene.cameras.main;
    // world point under cursor before zoom
    const o = this.viewOrigin();
    const worldX = o.x + screenX / cam.zoom;
    const worldY = o.y + screenY / cam.zoom;
    cam.zoom = newZoom;
    // re-align so the same world point sits under the cursor
    this.setViewOrigin(worldX - screenX / cam.zoom, worldY - screenY / cam.zoom);
    this.clampScroll();
  }

  private clampScroll() {
    const cam = this.scene.cameras.main;
    const viewW = cam.width / cam.zoom;
    const viewH = cam.height / cam.zoom;
    // The HUD bands (insets) count as extra room above and below the board, so a
    // board framed inside the free band is a legal camera position, not clamped away.
    const maxX = this.bounds.maxX - viewW;
    const maxY = this.bounds.maxY + this.insets.bottom / cam.zoom - viewH;
    const minX = this.bounds.minX;
    const minY = this.bounds.minY - this.insets.top / cam.zoom;
    const o = this.viewOrigin();
    const x = maxX <= minX ? (minX + maxX) / 2 : Phaser.Math.Clamp(o.x, minX, maxX);
    const y = maxY <= minY ? (minY + maxY) / 2 : Phaser.Math.Clamp(o.y, minY, maxY);
    this.setViewOrigin(x, y);
  }

  /** Screen space reserved for HUD at the top and bottom (design px). */
  private insets = { top: 0, bottom: 0 };

  /** Center the camera on the level and pick a zoom that frames it nicely. */
  frame(
    levelBounds: { minX: number; minY: number; maxX: number; maxY: number },
    insets: { top: number; bottom: number } = this.insets,
  ) {
    this.insets = insets;
    const cam = this.scene.cameras.main;
    const w = levelBounds.maxX - levelBounds.minX;
    const h = levelBounds.maxY - levelBounds.minY;
    const cx = (levelBounds.minX + levelBounds.maxX) / 2;
    const cy = (levelBounds.minY + levelBounds.maxY) / 2;
    const margin = 300;
    const fitX = cam.width / (w + margin);
    const usableH = Math.max(200, cam.height - insets.top - insets.bottom);
    const fitY = usableH / (h + margin);
    // The framing zoom is the truth: a board must open fully on screen. The zoom
    // floor follows it (never above it) so the player can always get back out this
    // far. Only an absurdly large board ever reaches HARD_MIN_ZOOM.
    const fit = Math.min(fitX, fitY, 1);
    cam.zoom = Phaser.Math.Clamp(fit, HARD_MIN_ZOOM, this.maxZoom);
    this.minZoom = Math.min(this.minZoom, cam.zoom);
    // Keep the board clear of the HUD: shift it into the free band between the
    // top block and the bottom controls.
    this.setViewOrigin(
      cx - cam.width / 2 / cam.zoom,
      cy - cam.height / 2 / cam.zoom - (insets.top - insets.bottom) / 2 / cam.zoom,
    );
    this.clampScroll();
  }

  /** Re-frame the camera on the whole level (used by the recenter control). */
  recenter() {
    this.frame(this.levelBounds);
  }

  /** True when the level content is too big to fit the viewport, i.e. worth panning. */
  canPan(): boolean {
    const { width, height } = this.scene.scale;
    const w = this.levelBounds.maxX - this.levelBounds.minX;
    const h = this.levelBounds.maxY - this.levelBounds.minY;
    return w > width * 0.98 || h > height * 0.98;
  }

  destroy() {
    this.bgZone.destroy();
    this.scene.input.off('pointermove');
    this.scene.input.off('pointerup');
    this.scene.input.off('pointerupoutside');
    this.scene.input.off('wheel');
  }
}
